import type { Action, Reducer } from 'redux';
import { IApplicationState, IAppThunkAction } from '.';
import IOutstandingBalance from '../interfaces/IOutstandingBalance';
import IPaymentGateway from '../interfaces/IPaymentGateway';
import { Routes, navigateTo } from '../helpers/routes';
import BalancePaymentService from '../service/balancePayments';
import { GET_PAYMENT_GATEWAYS_SUCCESS, GET_PAYMENT_GATEWAYS_COMPLETED, PaymentMethodTypes, actionCreators as PaymentActions } from './Payments';
import { actionCreators as UIActions } from './UI';
import { appInsights } from '../Logging/appInsights';

export type BalancePaymentLandingErrorType = 'paid' | 'notfound' | 'cancelled' | 'generic' | undefined;

export interface IBalancePaymentLandingState {
    booking: IOutstandingBalance | undefined;
    loading: boolean;
    polling: boolean;
    error: boolean;
    errorType: BalancePaymentLandingErrorType;
}

const FETCH_BALANCE_REQUESTED: 'FETCH_BALANCE_REQUESTED' = 'FETCH_BALANCE_REQUESTED';
export const FETCH_BALANCE_SUCCESS: 'FETCH_BALANCE_SUCCESS' = 'FETCH_BALANCE_SUCCESS';
const FETCH_BALANCE_FAILED: 'FETCH_BALANCE_FAILED' = 'FETCH_BALANCE_FAILED';
const BALANCE_POLL_STARTED: 'BALANCE_POLL_STARTED' = 'BALANCE_POLL_STARTED';
const BALANCE_POLL_STOPPED: 'BALANCE_POLL_STOPPED' = 'BALANCE_POLL_STOPPED';

const BALANCE_POLL_INTERVAL_MS = 5000;
const BALANCE_POLL_MAX_ATTEMPTS = 12;

interface IFetchBalanceRequestedAction extends Action {
    type: typeof FETCH_BALANCE_REQUESTED;
}

interface IFetchBalanceSuccessAction extends Action {
    type: typeof FETCH_BALANCE_SUCCESS;
    booking: IOutstandingBalance;
}

interface IFetchBalanceFailedAction extends Action {
    type: typeof FETCH_BALANCE_FAILED;
    errorType: BalancePaymentLandingErrorType;
}

interface IBalancePollStartedAction extends Action {
    type: typeof BALANCE_POLL_STARTED;
}

interface IBalancePollStoppedAction extends Action {
    type: typeof BALANCE_POLL_STOPPED;
}

export type KnownBalancePaymentLandingActions =
    | IFetchBalanceRequestedAction
    | IFetchBalanceSuccessAction
    | IFetchBalanceFailedAction
    | IBalancePollStartedAction
    | IBalancePollStoppedAction;

const KNOWN_ERROR_TYPES: ReadonlyArray<BalancePaymentLandingErrorType> = ['paid', 'notfound', 'cancelled', 'generic'];

const resolveErrorType = (error: Error): BalancePaymentLandingErrorType => {
    const serverErrorType = (error as Error & { errorType?: string }).errorType;
    if (serverErrorType && KNOWN_ERROR_TYPES.includes(serverErrorType as BalancePaymentLandingErrorType)) {
        return serverErrorType as BalancePaymentLandingErrorType;
    }
    return error.name?.includes('404') ? 'notfound' : 'generic';
};

export const actionCreators = {
    fetchBalance:
        (bookingRef: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: FETCH_BALANCE_REQUESTED });

            BalancePaymentService.GetOutstandingBalance(bookingRef)
                .then((booking) => {
                    dispatch({ type: FETCH_BALANCE_SUCCESS, booking });

                    const gatewayData: IPaymentGateway[] = booking.paymentGateways.map((gw) => ({
                        apiKey: gw.apiKey,
                        gateway: gw.gateway,
                        paymentMethodType: gw.paymentMethodType,
                    }));
                    dispatch({ type: GET_PAYMENT_GATEWAYS_SUCCESS, data: gatewayData });
                    dispatch({ type: GET_PAYMENT_GATEWAYS_COMPLETED });
                })
                .catch((error: Error) => {
                    const errorType = resolveErrorType(error);
                    dispatch({ type: FETCH_BALANCE_FAILED, errorType });
                    if (errorType !== 'paid' && errorType !== 'cancelled') {
                        appInsights?.trackException(
                            { error: new Error('Error fetching outstanding balance'), severityLevel: 2 },
                            { exception: error, sourceFile: 'BalancePaymentLanding.ts', product: 'booking' }
                        );
                    }                });
        },

    confirmBalancePaypal:
        (
            paypalOrderId: string,
            paypalCaptureId: string,
            payerEmail?: string,
            payerName?: string,
            paypalState?: string
        ): IAppThunkAction<any> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const booking = appState.balancePaymentLanding.booking;
            if (!booking) return;

            const metadata: { [key: string]: string } = {
                PayPalPayerEmail: payerEmail ?? '',
                PayPalPayerName: payerName ?? '',
                PayPalPaymentId: paypalOrderId,
                PayPalOrderId: paypalOrderId,
                PayPalCaptureId: paypalCaptureId,
                PayPalState: paypalState ?? '',
            };

            BalancePaymentService.ConfirmBalancePayment(booking.siteId, booking.bookingRef, {
                amount: booking.charge.total,
                currencyCode: booking.charge.currencyCode,
                dateCreated: new Date().toISOString(),
                emailAddress: payerEmail ?? '',
                fullName: payerName,
                phoneNumber: undefined,
                paymentGatewayMetadata: metadata,
                paymentMethodReference: paypalCaptureId,
                paymentMethodTypeId: Number(PaymentMethodTypes.Paypal),
                siteId: booking.siteId,
                tax: booking.charge.tax,
                adBlockEnabled: false,
            })
                .then(() => {
                    dispatch(PaymentActions.paypalPaymentSuccess());
                    dispatch(navigateTo(Routes.Complete, booking.siteUrlKey));
                })
                .catch((error: Error) => {
                    appInsights?.trackException(
                        { error: new Error('Error confirming balance paypal payment'), severityLevel: 2 },
                        {
                            exception: error,
                            bookingRef: booking.bookingRef,
                            paypalOrderId,
                            sourceFile: 'BalancePaymentLanding.ts',
                            product: 'booking',
                        }
                    );
                    dispatch(UIActions.displayError('errors.payments.error'));
                    dispatch(PaymentActions.paypalPaymentError());
                });
        },

    pollBalanceUntilPaid:
        (bookingRef: string): IAppThunkAction<any> =>
        (dispatch, getState) => {
            dispatch({ type: BALANCE_POLL_STARTED });

            let attempts = 0;

            const goToLanding = () => {
                dispatch(navigateTo(Routes.BalancePayment, bookingRef));
            };

            const tick = () => {
                attempts += 1;

                BalancePaymentService.GetOutstandingBalance(bookingRef)
                    .then(() => {
                        // 200 means the booking is still payable with an outstanding balance —
                        // the webhook hasn't landed yet. Keep polling until it does or we time out.
                        if (attempts >= BALANCE_POLL_MAX_ATTEMPTS) {
                            dispatch({ type: BALANCE_POLL_STOPPED });
                            // Webhook hasn't landed within the timeout — fall back to the
                            // landing page so the user can see the current state and retry.
                            goToLanding();
                            return;
                        }

                        window.setTimeout(tick, BALANCE_POLL_INTERVAL_MS);
                    })
                    .catch((error: Error) => {
                        const errorType = resolveErrorType(error);

                        if (errorType === 'paid') {
                            // Webhook landed; the booking is settled. Move the user to the complete page.
                            dispatch({ type: BALANCE_POLL_STOPPED });
                            const siteUrlKey = getState().balancePaymentLanding.booking?.siteUrlKey;
                            if (siteUrlKey) {
                                dispatch(navigateTo(Routes.Complete, siteUrlKey));
                            } else {
                                goToLanding();
                            }
                            return;
                        }

                        if (errorType === 'cancelled' || errorType === 'notfound') {
                            // Booking is no longer payable — surface the error on the landing page.
                            dispatch({ type: BALANCE_POLL_STOPPED });
                            goToLanding();
                            return;
                        }

                        appInsights?.trackException(
                            { error: new Error('Error polling outstanding balance'), severityLevel: 2 },
                            {
                                exception: error,
                                bookingRef,
                                sourceFile: 'BalancePaymentLanding.ts',
                                product: 'booking',
                            }
                        );

                        if (attempts >= BALANCE_POLL_MAX_ATTEMPTS) {
                            dispatch({ type: BALANCE_POLL_STOPPED });
                            goToLanding();
                            return;
                        }

                        window.setTimeout(tick, BALANCE_POLL_INTERVAL_MS);
                    });
            };

            window.setTimeout(tick, BALANCE_POLL_INTERVAL_MS);
        },
};

const unloadedState: IBalancePaymentLandingState = {
    booking: undefined,
    loading: false,
    polling: false,
    error: false,
    errorType: undefined,
};

export const reducer: Reducer<IBalancePaymentLandingState> = (
    state: IBalancePaymentLandingState | undefined,
    incomingAction: Action
): IBalancePaymentLandingState => {
    if (state === undefined) return unloadedState;

    const action = incomingAction as KnownBalancePaymentLandingActions;
    switch (action.type) {
        case FETCH_BALANCE_REQUESTED:
            return {
                ...state,
                loading: true,
                error: false,
                errorType: undefined,
            };
        case FETCH_BALANCE_SUCCESS:
            return {
                ...state,
                loading: false,
                booking: action.booking,
                error: false,
                errorType: undefined,
            };
        case FETCH_BALANCE_FAILED:
            return {
                ...state,
                loading: false,
                error: true,
                errorType: action.errorType,
            };
        case BALANCE_POLL_STARTED:
            return {
                ...state,
                polling: true,
            };
        case BALANCE_POLL_STOPPED:
            return {
                ...state,
                polling: false,
            };
        default:
            return state;
    }
};
